import { PaneRoute, PaneRouter, PaneRoutes } from "@/components/PaneRouter";
import { ROUTES } from "@/constants/routes";
import { RIGHT_PANEL_WIDTH_STORAGE_KEY } from "@/context/PaneLayoutWidthsContext";
import { useSidebarContext } from "@/context/SidebarContext";
import { AutomationPane } from "@/panes/automation/AutomationPane";
import { EmailDetailPane } from "@/panes/canvas-experiments/email-detail";
import { NotionDetailPane } from "@/panes/canvas-experiments/notion-detail";
import { ConnectionPane } from "@/panes/connection/ConnectionPane";
import { EntitiesPane } from "@/panes/entities";
import { EntityDetailPane } from "@/panes/entity-detail";
import { FileRecordDetailPane } from "@/panes/file-record-detail";
import {
  GitHubIssueDetailPane,
  GitHubProjectDetailPane,
  GitHubPullRequestDetailPane,
  GitHubRepoDetailPane,
} from "@/panes/github-detail";
import { CalendarEventDetailPane } from "@/panes/google-calendar-detail";
import { GoogleDriveFileDetailPane } from "@/panes/google-drive-detail";
import { GranolaMeetingDetailPane } from "@/panes/granola-detail";
import { SessionPane } from "@/panes/session";
import { SkillTemplatePane } from "@/panes/skill-template/SkillTemplatePane";
import { SkillPane } from "@/panes/skill/SkillPane";
import { SlackMessageDetailPane } from "@/panes/slack-detail";
import type { LocationState } from "@/types/pane-routing";
import { cn } from "@/utils";
import {
  ResizablePanel,
  type ResizablePanelHandle,
} from "@cohesive/ui-components";
import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useWindowSize } from "usehooks-ts";

export function RightPaneRouter({
  maxPaneWidth,
  isRightPanelOpen,
  onRightPanelWidthChange,
  setIsExpanded,
  isExpanded,
  leftChatDockPx,
  leftRailWidth = 0,
}: {
  maxPaneWidth: number;
  isRightPanelOpen: boolean;
  onRightPanelWidthChange: (width: number) => void;
  setIsExpanded: (value: boolean) => void;
  isExpanded: boolean;
  /** Left space reserved by a docked canvas chat; shrinks the expanded pane by this much. */
  leftChatDockPx: number;
  /**
   * Horizontal space the left rail column actually occupies in the layout row
   * (0 when the rail is collapsed); subtracted when expanding.
   */
  leftRailWidth?: number;
}) {
  // Live window width so the expanded pane tracks window resizes; a stale
  // snapshot here is what used to leave the pane too narrow or wider than
  // the viewport.
  const { width: windowWidth } = useWindowSize();

  const leftOffset = leftRailWidth + leftChatDockPx;
  const expandedWidth = Math.max(400, windowWidth - leftOffset);

  // Per-frame tracking of rail drags while expanded. `leftRailWidth` is React
  // state the rail commits only at drag END (deliberate — live frames go
  // through the imperative nav-zone channel so drags don't re-render the
  // world), so during the drag this component's `expandedWidth` is stale and
  // the canvas peeked through between the chat dock (which tracks live) and
  // this pane. Follow the same imperative philosophy: subscribe to the rail's
  // live frames and write the width straight to the panel node — zero React
  // renders per frame; the drag-end state re-render lands on the same number.
  const { subscribeRailLiveWidth, railResizing } = useSidebarContext();
  const leftChatDockPxRef = useRef(leftChatDockPx);
  leftChatDockPxRef.current = leftChatDockPx;
  useEffect(() => {
    if (!isExpanded || !isRightPanelOpen) return;
    return subscribeRailLiveWidth((liveRailWidth) => {
      const el = document.querySelector<HTMLElement>(".js-right-pane-panel");
      if (!el) return;
      const width = Math.max(
        400,
        window.innerWidth - liveRailWidth - leftChatDockPxRef.current,
      );
      el.style.width = `${width}px`;
    });
  }, [isExpanded, isRightPanelOpen, subscribeRailLiveWidth]);

  useEffect(() => {
    if (!isRightPanelOpen) {
      setIsExpanded(false);
    }
  }, [isRightPanelOpen, setIsExpanded]);

  // An opener may ask for a width with a one-shot `rightPaneWidth` hint.
  // Apply it once the pane is open, then strip it so a later drag sticks and
  // ordinary opens keep the stored width.
  const panelRef = useRef<ResizablePanelHandle>(null);
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const state = location.state as LocationState | null;
    if (!state?.rightPaneWidth || !isRightPanelOpen) return;

    panelRef.current?.setWidth(state.rightPaneWidth);

    const rest = { ...state };
    delete rest.rightPaneWidth;
    navigate(location.pathname + location.search, {
      replace: true,
      state: rest,
    });
  }, [location, isRightPanelOpen, navigate]);

  // Expansion is a render-time override (`renderedWidth`), so the stored width
  // never changes while expanded. Consumers still need the *effective* width:
  // remember the stored width and report whichever one is currently rendered.
  const lastStoredWidthRef = useRef(0);
  const isExpandedRef = useRef(isExpanded);
  isExpandedRef.current = isExpanded;

  const handleWidthChange = useCallback(
    (width: number) => {
      lastStoredWidthRef.current = width;
      if (!isExpandedRef.current) {
        onRightPanelWidthChange(width);
      }
    },
    [onRightPanelWidthChange],
  );

  useEffect(() => {
    onRightPanelWidthChange(
      isExpanded ? expandedWidth : lastStoredWidthRef.current,
    );
  }, [isExpanded, expandedWidth, onRightPanelWidthChange]);

  return (
    <ResizablePanel
      ref={panelRef}
      directions={isExpanded ? [] : ["left"]}
      isClosed={!isRightPanelOpen}
      minWidth={400}
      maxWidth={maxPaneWidth}
      renderedWidth={isExpanded ? expandedWidth : undefined}
      // Beside a left chat dock the expanded width follows the dock's LIVE
      // drag width — the panel's width transition would trail the dock and
      // flash the squeezed content region in the gap, so snap instead. The
      // same applies during a rail drag: the live rail-width subscriber
      // writes this panel's width per frame, and easing those writes is
      // exactly the trailing gap it exists to remove. The gates flip off in
      // the same commits their sources reset, so un-docking, drag release
      // and collapse-to-default keep their animation.
      transitionDuration={
        isExpanded && (leftChatDockPx > 0 || railResizing) ? 0 : undefined
      }
      storageKey={RIGHT_PANEL_WIDTH_STORAGE_KEY}
      onWidthChange={handleWidthChange}
      showHandleIndicator
      // overflow-visible so the resize handle pill (which straddles the left
      // edge) isn't clipped over the canvas; same opt-in the left rail uses.
      // It applies only while the panel is open — ResizablePanel re-clips
      // itself whenever the content is pinned oversized (closed/animating).
      // js- marker: the live rail-width subscriber writes width imperatively,
      // and the e2e pane page objects (packages/e2e/panes/base-pane.ts) locate
      // the pane by it — keep it on the panel regardless of expand state.
      // Expanded, the pane sits flush against a left neighbor that already
      // draws the seam (the rail's or the left dock's border-r — the left
      // neighbor owns a flush seam, same rule the rail documents for its own
      // edge), so drawing border-l too made this the app's only 2px seam and
      // left the handle pill looking off-center on it. Floating over the
      // canvas there is no neighbor, so the pane draws its own edge.
      className={cn(
        "js-right-pane-panel bg-surface-rail h-full overflow-visible",
        !isExpanded && "border-l",
      )}
    >
      <PaneRouter pane="rightPane">
        <PaneRoutes>
          <PaneRoute path={ROUTES.SESSIONS.SESSION} element={<SessionPane />} />
          <PaneRoute
            path={ROUTES.FILE_RECORDS.DETAIL}
            element={<FileRecordDetailPane />}
          />
          <PaneRoute path={ROUTES.ENTITIES.LIST} element={<EntitiesPane />} />
          <PaneRoute
            path={ROUTES.ENTITIES.DETAIL}
            element={<EntityDetailPane />}
          />
          <PaneRoute
            path={ROUTES.CANVAS_EXPERIMENTS.NOTION_DETAIL}
            element={<NotionDetailPane />}
          />
          <PaneRoute
            path={ROUTES.CANVAS_EXPERIMENTS.EMAIL_DETAIL}
            element={<EmailDetailPane />}
          />
          <PaneRoute
            path={ROUTES.GOOGLE_DRIVE.FILE_DETAIL}
            element={<GoogleDriveFileDetailPane />}
          />
          <PaneRoute
            path={ROUTES.GITHUB.ISSUE_DETAIL}
            element={<GitHubIssueDetailPane />}
          />
          <PaneRoute
            path={ROUTES.GITHUB.PR_DETAIL}
            element={<GitHubPullRequestDetailPane />}
          />
          <PaneRoute
            path={ROUTES.GITHUB.REPO_DETAIL}
            element={<GitHubRepoDetailPane />}
          />
          <PaneRoute
            path={ROUTES.GITHUB.PROJECT_DETAIL}
            element={<GitHubProjectDetailPane />}
          />
          <PaneRoute
            path={ROUTES.GOOGLE_CALENDAR.EVENT_DETAIL}
            element={<CalendarEventDetailPane />}
          />
          <PaneRoute
            path={ROUTES.SLACK.MESSAGE_DETAIL}
            element={<SlackMessageDetailPane />}
          />
          <PaneRoute
            path={ROUTES.GRANOLA.MEETING_DETAIL}
            element={<GranolaMeetingDetailPane />}
          />
          <PaneRoute path={ROUTES.SKILLS.SHOW} element={<SkillPane />} />
          <PaneRoute
            path={ROUTES.SKILL_TEMPLATES.SHOW}
            element={<SkillTemplatePane />}
          />
          <PaneRoute
            path={ROUTES.AUTOMATIONS.SHOW}
            element={<AutomationPane />}
          />
          <PaneRoute
            path={ROUTES.SETTINGS.INTEGRATION_SETTINGS}
            element={<ConnectionPane />}
          />
        </PaneRoutes>
      </PaneRouter>
    </ResizablePanel>
  );
}
